import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListFilterPayload } from '@app/helpers';
import { environment } from '@environments/environment';
import {
    AddUser,
    AddUserResponse,
    BulkUploadBody,
    CsvUserError,
    CsvUserJSON,
    DownloadUsersPayload,
    GeneralDataResponse,
    GeneralResponse,
    GetOrgUserList,
    GetUser,
    OrgUserData,
    UserDemographicFields,
    UserFormData,
    UserInterest
} from '@app/models/user.model';
import { FORM_REGEX, PhoneInputFieldData, CSV_SNAP_HEADERS, CSV_USERS_HEADERS, CSV_CAREGIVER_HEADERS, BULK_UPLOAD, CLIENT_PROFILE_FORM_CHAR_LIMITS } from '@app/shared';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { SeniorCenter } from '@app/models/organization.model';
import * as moment from 'moment-timezone';

@Injectable()
export class UsersService {
    userDetails!: UserFormData;
    private userInterests!: UserInterest[];
    private readonly _userRaces: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    public userCsvRecords: any[] = [];
    public userCsvErrors: CsvUserError[] = [];
    _orgPrograms: SeniorCenter[] = [];
    private _orgLanguages: string[] = [];

    constructor(private readonly _httpService: HttpService) { }

    get userRacesData(): Observable<string[]> {
        return this._userRaces;
    }

    setUserRaces(data: string[]) {
        this._userRaces.next(data);
    }

    set orgLanguages(languages: string[]) {
        this._orgLanguages = [...languages];
    }

    getOrgUserWithParams(orgId: number, center?: number, userParams?: HttpParams): Observable<GetOrgUserList> {
        if (!userParams && center) {
            userParams = new HttpParams();
            userParams = userParams.set('center', center);
        } else if (userParams && center) {
            userParams = userParams.set('center', center);
        }
        return this._httpService.getOrgUsersWithParams(orgId, userParams);
    }

    getOrgUserWithFilters(orgId: number, center?: number, userParams?: ListFilterPayload): Observable<GetOrgUserList> {
        if (!userParams && (center != null)) {
            userParams = {center: center};
        } else if (userParams && (center != null)) {
            userParams = { ...userParams, center: center };
        }
        return this._httpService.getOrgUsersWithFilters(orgId, userParams);
    }

    setInterest(interests: UserInterest[]) {
        this.userInterests = interests;
    }

    getUserById(orgId: number, center: number, memberId: string): Observable<UserFormData> {
        return this._httpService.getUserByMemberId(orgId, center, memberId).pipe(map(res => this.convertToFormData(res.data, center)));
    }

    userChanges(isAddMethod: boolean, userData: any, isOrgUsers: boolean = false) {

        // V1 APIs for users
        if (!isOrgUsers) {
            if (isAddMethod) {
                return this.addUser(userData);
            }

            return this.updateUser(userData);
        }

        // V2 APIs for users
        if (isAddMethod) {
            return this.addOrgUserV2(userData);
        }

        return this.updateOrgUserV2(userData);
    }

    addUser(newUser: AddUser): Observable<AddUserResponse> {
        return this._httpService.postNewUser(newUser);
    }

    updateUser(modifiedUser: any): Observable<any> {
        return this._httpService.updateUser(this.userDetails.member_ref, modifiedUser).pipe(map(res => res.data));
    }

    deleteUserById(memberId: string): Observable<GeneralResponse> {
        return this._httpService.deleteUserByMemberId(memberId);
    }

    getUserDownloadLink(organizationId: number, downloadPayload: DownloadUsersPayload, isOrgUserFeatureFlag: boolean = false) {
        if (isOrgUserFeatureFlag && (downloadPayload.center != null)) {
            downloadPayload.program = [downloadPayload.center];
            downloadPayload.center = undefined;
        }
        return !isOrgUserFeatureFlag 
            ? this._httpService.getUserDownloadLink(organizationId, {...downloadPayload}) 
            : this._httpService.getUserDownloadLinkV2(organizationId, {...downloadPayload});
    }

    /**Convert the user data from API
     * to userFormdata type to patch in user form
     */
    convertToFormData(user: GetUser, centerId?: number): UserFormData {
        const clientDeliveryMethod = user.client_delivery_method ?? {};
        const dm: string[] = Object.keys(clientDeliveryMethod).filter(key => clientDeliveryMethod[key]);

        const userFormData: UserFormData = {
            id: user.id,
            active: user.active,
            centerId: centerId,
            member_ref: user.member_ref,
            client_id: user?.client_id, 
            fname: user.fname,
            lname: user.lname,
            mname: user.mname,
            age: user.age == 0 ? null : user.age,
            gender: user.gender?.trim().toLowerCase(),
            phone_no: this.dividePhoneNumber(user.phone_no),
            landline: this.dividePhoneNumber(user.landline),
            email: user.email,
            lang: user.lang,
            interests: user.UserInterests,
            delivery_method: dm,
            welcome_sent: user.welcome_sent,
            zipcode: user.zipcode,
            birthdate: user.birthdate,
            race: user.race,
            disable: user.disable ?? 0,
            veteran: user.veteran ?? 0,
            notes: user.notes,
            snap_enrollment: user.user_snap?.snap_enrollment == null ? 0 : user.user_snap?.snap_enrollment,
            snap_eligibility: user.user_snap?.snap_eligibility == null ? 0 : user.user_snap?.snap_eligibility,
            snap_interest: user.user_snap?.snap_interest == null ? 0 : user.user_snap?.snap_interest,
            ph_interested: user.user_caregiver_interest?.ph_interested == null ? 0 : user.user_caregiver_interest?.ph_interested,
            ph_consented: user.user_caregiver_interest?.ph_consented == null ? 0 : user.user_caregiver_interest?.ph_consented,
            ph_info_provided: user.user_caregiver_interest?.ph_info_provided == null ? 0 : user.user_caregiver_interest?.ph_info_provided,
            date_activated: user.date_activated,
            date_deactive: user.date_deactive,
            date_unsub: user.date_unsub,
            unsubscribed: user.unsubscribed,
            self_caregiver: user.self_caregiver ?? 0,
            cbo_active: user.cbo_active, 
            ClientCenters: user.ClientCenters, 
            programIds: user.ClientCenters?.map(data => data?.SeniorCenter?.id) || undefined,
            UserLabels: user.UserLabels,
            customFields: (user.customFields ?? []).sort((a, b) => a.field_id - b.field_id),
            caregiver_id: user.caregiver_id ?? null,
            caregiver_details: user.caregiver_details ?? null,
            caregiver_for: user.caregiver_for ?? [],
            primary_user: user.primary_user ?? null,
            household_members: user.household_members ?? [],
            ...user.medicaidInfo,
        };
        if (user.consent) {
            userFormData.user_consent = !!user.consent['obtanined_user_consent'];
            userFormData.obtained_date = new Date(user.consent['consent_obtained_Date']);
            userFormData.terms_policy = !!user.consent['explained_privacy_terms_policy'];
        }
        this.userDetails = userFormData;

        return userFormData;
    }

    dividePhoneNumber(value: string): PhoneInputFieldData {
        if (value) {
            const num = value.startsWith('+') ? value.slice(value.length - 10) : value;
            return new PhoneInputFieldData(num);
        }

        return new PhoneInputFieldData('');
    }

    /**Upload Bulk user data in the form of JSON */
    bulkUploadUser(orgCode: string, body: BulkUploadBody) {
        return this._httpService.userBulkUploadV2(orgCode, body).pipe(map(res => res));
    }

    /**Upload Bulk user data in the form of JSON */
    fetchBulkUploadStatus(orgCode: string, id?:string) {
        let params = new HttpParams();
        if (id) {
            params = params.set("uploadId", id);
        }
        return this._httpService.bulkUploadStatusV2(orgCode, params).pipe(map(res => res));
    }
    fetchCSVFile(id: string, orgCode: string, type: string) {
        return this._httpService.bulkUploadCsvV2(id, orgCode, type).pipe(map(res => res));
    }

    fetchCSVFileAsBlob(id: string, orgCode: string, type: string): Observable<Blob> {
        return this._httpService.getBulkUploadFilesAsblob(id, orgCode, type);
    }

    /**Read the uploaded file
     * split the data by lines and spaces-line
     * invoke getDataRecordsArrayFromCSVFile()
     * invoke the result() with records of user
     * if errors occurs while reading file logs the error
     */

    fetchCSVFileLinks(org_code: string, snap: boolean, medicaid_enabled: boolean, customFieldsUsed: boolean, samplePaths?: any): { blankLink: string, filledLink: string } {
        let blankLink = null;
        let filledLink = null;
        const bulkUploadPaths = samplePaths ?? BULK_UPLOAD;
        let bucketName = bulkUploadPaths.CUSTOM_FILES_BUCKET_NAME;
        bucketName += environment?.suffix? ('_' + environment.suffix) : '';
        let basePath = bulkUploadPaths.CUSTOM_FILES_PUBLIC_ACCESS_BASE_URL + bucketName + '/' + org_code + `/${org_code}_`;

        switch (true) {
            case snap && medicaid_enabled:
                if(customFieldsUsed) {
                    blankLink = basePath + bulkUploadPaths.BLANK_MEDICAID_WITH_SNAP_FILE_NAME;
                    filledLink = basePath + bulkUploadPaths.SAMPLE_MEDICAID_WITH_SNAP_FILE_NAME;
                 } else {
                    blankLink = bulkUploadPaths.BLANK_MEDICAID_WITH_SNAP_FILE_LINK;
                    filledLink = bulkUploadPaths.SAMPLE_MEDICAID_WITH_SNAP_FILE_LINK;
                }
                break;
            case snap:
                if(customFieldsUsed) {
                    blankLink = basePath + bulkUploadPaths.BLANK_SNAP_FILE_NAME;
                    filledLink = basePath + bulkUploadPaths.SAMPLE_SNAP_FILE_NAME;
                 } else {
                blankLink = bulkUploadPaths.BLANK_SNAP_FILE_LINK;
                filledLink = bulkUploadPaths.SAMPLE_SNAP_FILE_LINK;
                 }
                break;
            case medicaid_enabled:
                if(customFieldsUsed) {
                    blankLink = basePath + bulkUploadPaths.BLANK_MEDICAID_FILE_NAME;
                    filledLink = basePath + bulkUploadPaths.SAMPLE_MEDICAID_FILE_NAME;
                 } else {
                blankLink = bulkUploadPaths.BLANK_MEDICAID_FILE_LINK;
                filledLink = bulkUploadPaths.SAMPLE_MEDICAID_FILE_LINK;
                 }
                break;
            default:
                if(customFieldsUsed) {
                    blankLink = basePath + bulkUploadPaths.BLANK_FILE_NAME;
                    filledLink = basePath + bulkUploadPaths.SAMPLE_FILE_NAME;
                 } else {
                blankLink = bulkUploadPaths.BLANK_FILE_LINK;
                filledLink = bulkUploadPaths.SAMPLE_FILE_LINK;
                 }
                break;
        }

        return { blankLink, filledLink };
    }

    matchHeaders(
        snap: boolean,
        data: string,
        medicaid_enabled: boolean
    ): string | null {
        const EXPECTED_HEADERS: { [key: string]: number } = {
            "First Name": 1,
            "Middle Name (Optional)": 2,
            "Last Name": 3,
            "Gender (Optional)": 4,
            "Primary Phone (Text or Voice Calls)": 5,
            "Secondary Phone (Optional)": 6,
            "Email": 7,
            "Delivery SMS": 8,
            "Delivery Voice": 9,
            "Delivery Email": 10,
            "Interests (Comma Separated)": 11,
            "Preferred Language": 12,
            "Date of Birth (mm/dd/yyyy) (Optional)": 13,
            "Zip Code (Optional)": 14,
            "Race/Ethnicity (Optional)": 15,
            "Disability (Optional)": 16,
            "Veteran (Optional)": 17,
            "Notes (Optional)": 18,
            "Client ID (Optional)": 19,
            "Program(s) (Optional)": 20,
        };

        if(snap && medicaid_enabled){
            
            EXPECTED_HEADERS["SNAP_Eligible (Optional)"] = 21;
            EXPECTED_HEADERS["SNAP_Enrolled (Optional)"] = 22;
            EXPECTED_HEADERS["SNAP_Interest (Optional)"] = 23;

            EXPECTED_HEADERS["Medicaid ID (Optional)"] = 24;
            EXPECTED_HEADERS["MCO Enrollment Date (mm/dd/yyyy) (Optional)"] = 25;
            EXPECTED_HEADERS["MCO Disenrollment Date (mm/dd/yyyy) (Optional)"] =  26;

        }else if(snap){
            EXPECTED_HEADERS["SNAP_Eligible (Optional)"] = 21;
            EXPECTED_HEADERS["SNAP_Enrolled (Optional)"] = 22;
            EXPECTED_HEADERS["SNAP_Interest (Optional)"] = 23;
        }else if (medicaid_enabled){
            EXPECTED_HEADERS["Medicaid ID (Optional)"] = 21;
            EXPECTED_HEADERS["MCO Enrollment Date (mm/dd/yyyy) (Optional)"] = 22;
            EXPECTED_HEADERS["MCO Disenrollment Date (mm/dd/yyyy) (Optional)"] = 23;
        }

        // Caregiver columns always follow all other optional columns
        const caregiverStart = Math.max(...Object.values(EXPECTED_HEADERS)) + 1;
        EXPECTED_HEADERS["Caregiver Name (Optional)"] = caregiverStart;
        EXPECTED_HEADERS["Caregiver Email (Optional)"] = caregiverStart + 1;
        EXPECTED_HEADERS["Caregiver Phone Number (Optional)"] = caregiverStart + 2;
    
        const FILE_HEADERS = data.split(",").map(header => header.trim());

        for (const [header, position] of Object.entries(EXPECTED_HEADERS)) {
            const fileHeader = FILE_HEADERS[position - 1]?.trim();
            if (!fileHeader) {
                return `Header mismatch at position ${position}: Expected '${header}', but found empty.`;
            } else if (fileHeader.toLowerCase() !== header.toLowerCase()) {
                return `Incorrect header at position ${position}: Expected '${header}', but found '${fileHeader}'.`;
            }
        }
    
        return null;
    }

    isEmptyRow(data: string): boolean {
        return (data || "").split(",").join("").split(" ").join("").length == 0;
    }

    uploadListener(orgId: number, snap: boolean, csvFile: File, result: any, error: any, medicaid_enabled: boolean): void {
        this.userCsvRecords = [];
        this.userCsvErrors = [];
        let reader = new FileReader();
        reader.readAsText(csvFile);

        reader.onload = () => {
            let csvData = reader.result;
            if (csvData) {
                let csvRecordsArray = csvData
                    .toString()
                    .trim()
                    .split(/\r\n|\n/);

                const headersError = this.matchHeaders(snap, csvRecordsArray[0], medicaid_enabled);

                if (csvFile.type === 'text/csv' && csvRecordsArray.length >= 2 && !headersError && !this.isEmptyRow(csvRecordsArray[1])) {
                    return result()
                } else {
                    let errorType = null;
                    if (csvFile.type !== 'text/csv') {
                        errorType = 'Selected file is not a correct CSV file.'
                    } else if (csvRecordsArray.length < 2 || this.isEmptyRow(csvRecordsArray[1])) {
                        errorType = 'CSV file must have at least 1 records to upload.';
                    } else {
                        errorType = headersError;
                    }
                    return error(errorType);
                }
            } else {
                return error();
            }
        };
        reader.onerror = function () {
            console.log('somthing went wrong, try again');
        };
    }


    isCurrentArrayValid(csvRecordArray: string[], snap: boolean): boolean {
        let fileError = new CsvUserError();
        if (csvRecordArray.length > 501) {
            fileError.value = 'Users';
            fileError.message = 'CSV file have maximum limit 500 users to upload';
            this.userCsvErrors.push(fileError);
            return true;
        } else if (csvRecordArray.length <= 1) {
            fileError.value = 'Users';
            fileError.message = 'CSV file must have at least 1 records to upload';
            this.userCsvErrors.push(fileError);
            return true;
        } else {
            let realArray = CSV_USERS_HEADERS;
            if (snap) 
            {
                realArray = realArray.concat(CSV_SNAP_HEADERS);
            }
            realArray = realArray.concat(CSV_CAREGIVER_HEADERS);

            let currentArray = csvRecordArray[0].split(',');
            currentArray = currentArray.map(ca => ca.replace(/\s/g, '').toLowerCase());
            if (currentArray && realArray) {
                if (realArray.length != currentArray.length || !realArray.every(ca => currentArray.includes(ca))) {
                    let headerError = new CsvUserError();
                    headerError.column = '';
                    headerError.row = 1;
                    headerError.label = 'Header';
                    headerError.message = ' in the csv file is not matching the format';
                    headerError.value = 'Header Error';
                    this.userCsvErrors.push(headerError);
                    return true;
                }
            }
            return false;
        }
    }

    /**loop the array of long string(user details)
     * split by ',' and assign to curruntRecord
     * inoke getInsterestsFromCSV() to get the value string of interests
     * create new UserJSON object by passing required values and curruntRecord array
     * push the userJSON objects to array of csv record and return
     */
    getDataRecordsArrayFromCSVFile(orgId: number, csvRecordsArray: any, snap: boolean) {
        let csvArr = [];
        let isInValidRecord: boolean = this.isCurrentArrayValid(csvRecordsArray, snap);
        if (isInValidRecord) return [];
        for (let i = 1; i < csvRecordsArray.length; i++) {
            if (csvRecordsArray[i]?.length && csvRecordsArray[i].replaceAll(',', '')?.length) {
                csvArr.push(this.checkUserDataValid(orgId, csvRecordsArray[i].split('"'), i + 1, snap));
            }
        }
        this.checkDuplicateNumberErrors(csvArr);

        
        return csvArr;
    }

    checkDuplicateNumberErrors(data: CsvUserJSON[]) {
        let orgnlPhoneNumberData: CsvUserJSON[] = [];

        let duplicateNumberError: CsvUserError = { value: '', column: '5', row: 0, message: ' Duplicate Error', label: 'Phone Number' };

        for (let i = 0; i < data.length; i++) {
            if (data[i].phone_no?.length) {
                let matchedData = orgnlPhoneNumberData.find(user => user.phone_no == data[i].phone_no);
                if (!matchedData) {
                    orgnlPhoneNumberData.push(data[i]);
                }
                else {
                    let orginalDataIndex = data.findIndex(user => user.phone_no == matchedData?.phone_no);
                    this.userCsvErrors.push({ ...duplicateNumberError, value: matchedData.phone_no.replace('+1', ''), row: orginalDataIndex + 2 });
                    this.userCsvErrors.push({ ...duplicateNumberError, value: data[i].phone_no.replace('+1', ''), row: i + 2 });
                }
            }
        }
    }
    splitUserRecords(userRecord: string[]): {
        rowData: string[];
        splittedInterest: string;
        selectedInterest: string[];
        selectedRace: string;
        selectedPrograms: string;
    } {
        let rowData: string[];
        let splittedInterest = '';
        let selectedInterest: string[] = [];
        let selectedRace: string = '';
        let selectedPrograms: string = '';        

        switch (userRecord.length) {
            case 3:
                rowData = userRecord[0].concat(userRecord[2]).split(',');
                if (userRecord[0].split(",").length == 11) {
                    splittedInterest = userRecord[1];
                    selectedRace = rowData[14];
                    selectedPrograms = rowData[19];
                } else if (userRecord[0].split(",").length == 15) {
                    splittedInterest = rowData[10];
                    selectedRace = userRecord[1];
                    selectedPrograms = rowData[19];
                } else if (userRecord[0].split(",").length == 20) {
                    splittedInterest = rowData[10];
                    selectedRace = rowData[14];
                    selectedPrograms = userRecord[1];
                }                
                break;

            case 5:   
                rowData = userRecord[0].concat(userRecord[2]).concat(userRecord[4]).split(',');
                if (userRecord[0].split(",").length == 11) {
                    splittedInterest = userRecord[1];

                    if (userRecord[2].split(",").length == 5) {
                        selectedRace = userRecord[3];
                        selectedPrograms = rowData[19];
                    } else {
                        selectedPrograms = userRecord[3];
                        selectedRace = rowData[14];
                    }
                } else if (userRecord[0].split(",").length == 15) {
                    selectedRace = userRecord[1];
                    splittedInterest = rowData[10];
                    selectedPrograms = userRecord[3];
                }         
                break;
            case 7: 
                splittedInterest = userRecord[1];
                selectedRace = userRecord[3];
                selectedPrograms = userRecord[5];
                rowData = userRecord[0].concat(userRecord[2]).concat(userRecord[4].concat(userRecord[6])).split(',');
                break;

            default:
                rowData = userRecord[0].split(',');
                splittedInterest = rowData[10];
                selectedRace = rowData[14];
                selectedPrograms = rowData[19];
                break;
        }

        if (splittedInterest.length) selectedInterest = splittedInterest.split(',').map(i => i.trim());

        return {
            rowData,
            splittedInterest,
            selectedInterest,
            selectedRace,
            selectedPrograms,
        };
    }

    getProgramIds(selectedPrograms: string, rownumber: number): number[] {
        let formatedSelectedPrograms = selectedPrograms.split(",").map(i => i.replace(/\s/g, '').toLowerCase());
        let isValidPrograms = false;
        if (!formatedSelectedPrograms?.length || formatedSelectedPrograms.every(fsi => !fsi.trim().length)) {
            isValidPrograms = true;
        } else {
            isValidPrograms = formatedSelectedPrograms?.every(fsi => this._orgPrograms.some(ui => fsi == (ui.name.replace(/\s/g, '').toLowerCase())));
        }
        if (!isValidPrograms) {
            let programError = new CsvUserError();
            programError.message = ' are not valid.';
            programError.value = selectedPrograms;
            this.userCsvErrors.push({ ...programError, label: 'Programs', row: rownumber, column: '20' });
        }

        let programIds: number[] = [];
        formatedSelectedPrograms.forEach(pName => {
            const sProgram = this._orgPrograms.find(orgPrgm => (orgPrgm.name.replace(/\s/g, '').toLowerCase()) == pName);
            if (sProgram?.id) {
                programIds.push(sProgram.id);
            }
        });

        return programIds;
    }

    getInterestIds(selectedInterest: string[], splittedInterest: string, rownumber: number): number[] {
        let formatedSelectedInterest = selectedInterest.map(i => i.replace(/\s/g, '').toLowerCase());

        let isValidInterest = false;
        if (!formatedSelectedInterest?.length || formatedSelectedInterest.every(fsi => !fsi.trim().length))
            isValidInterest = true;
        else
            isValidInterest = formatedSelectedInterest?.every(fsi => this.userInterests.some(ui => fsi == (ui.name.replace(/\s/g, '').toLowerCase())));

        if (!isValidInterest) {
            let interestError = new CsvUserError();
            interestError.message = ' are not valid.';
            interestError.value = splittedInterest;
            this.userCsvErrors.push({ ...interestError, label: 'Interests', row: rownumber, column: '11' });
        }

        return this.userInterests.filter(i => selectedInterest.includes(i.name)).map(i => i.id);
    }

    getSnapValues(rowData: string[], rownumber: number) {
        let snap: {
            snap_enrollment?: boolean | null;
            snap_eligibility?: boolean | null;
            snap_interest?: boolean | null;
        } = {};

        const validateSnap = (snapValue: string, snapLabel: string, row: number, column: string) => {
            let snapError = this.validationCheckError(snapValue, false, 0, 0, FORM_REGEX.snap);
            if (snapError) this.userCsvErrors.push({ ...snapError, label: snapLabel, row: row, column: column });
        }

        let snapEligible = rowData[20];
        if (snapEligible) {
            validateSnap(snapEligible, 'SNAP Eligible', rownumber, '21');

            if (snapEligible == "Yes") {
                snap.snap_eligibility = true;
            }
            else if (snapEligible == "No") {
                snap.snap_eligibility = false;
            }
        }

        let snapEnroll = rowData[21];
        if (snapEnroll) {
            validateSnap(snapEligible, 'SNAP Enrolled', rownumber, '22');

            if (snapEnroll == "Yes") {
                snap.snap_enrollment = true;
            }
            else if (snapEnroll == "No") {
                snap.snap_enrollment = false;
            }
        }

        let snapInterest = rowData[22];
        if (snapInterest) {
            validateSnap(snapEligible, 'SNAP Interest', rownumber, '23');

            if (snapInterest == "Yes") {
                snap.snap_interest = true;
            }
            else if (snapInterest == "No") {
                snap.snap_interest = false;
            }
        }
        return snap;
    }

    /**Check the records if they are passing database validations.**/
    checkUserDataValid(orgId: number, userRecord: string[], rownumber: number, snap: boolean) {
        let {
            rowData,
            splittedInterest,
            selectedInterest,
            selectedRace,
            selectedPrograms,
        } = { ...this.splitUserRecords(userRecord) };

        let currentRecord: CsvUserJSON = new CsvUserJSON();
        currentRecord.orgId = orgId;
        currentRecord.programIds = this.getProgramIds(selectedPrograms, rownumber);

        let fnameError = this.validationCheckError(rowData[0], true, CLIENT_PROFILE_FORM_CHAR_LIMITS.F_NAME_MAX);
        this.pushFieldError(fnameError, 'First Name', rownumber, '1');
        currentRecord.fname = rowData[0];

        let mnameError = this.validationCheckError(rowData[1], false, CLIENT_PROFILE_FORM_CHAR_LIMITS.M_NAME_MAX);
        this.pushFieldError(mnameError, 'Middle Name', rownumber, '2');
        currentRecord.mname = rowData[1];

        let lnameError = this.validationCheckError(rowData[2], true, CLIENT_PROFILE_FORM_CHAR_LIMITS.L_NAME_MAX);
        this.pushFieldError(lnameError, 'Last Name', rownumber, '3');
        currentRecord.lname = rowData[2];

        let genderError = this.validationCheckError(rowData[3], false, 0, 0, FORM_REGEX.gender);
        this.pushFieldError(genderError, 'Gender', rownumber, '4');
        const normalizedGender = rowData[3].trim().toString().toLowerCase();
        const nonBinaryPattern = /non-binary/;
        const gdrValue = nonBinaryPattern.exec(normalizedGender)
        ? 'nonbinary'
        : normalizedGender;
        currentRecord.gender = rowData[3].length ? gdrValue : 'unspecified';

        let smsError = this.validationCheckError(rowData[7], false, 0, 0, FORM_REGEX.deliveryType);
        this.pushFieldError(smsError, 'Delivery Type SMS', rownumber, '8');
        currentRecord.client_delivery_method.checkin_sms = rowData[7].trim().toString().toLowerCase() === 'yes';

        let voiceError = this.validationCheckError(rowData[8], false, 0, 0, FORM_REGEX.deliveryType);
        this.pushFieldError(voiceError, 'Delivery Type Voice', rownumber, '9');
        currentRecord.client_delivery_method.checkin_voice = rowData[8].trim().toString().toLowerCase() === 'yes';

        let emailError = this.validationCheckError(rowData[9], false, 0, 0, FORM_REGEX.deliveryType);
        this.pushFieldError(emailError, 'Delivery Type Email', rownumber, '10');
        currentRecord.client_delivery_method.checkin_email = rowData[9].trim().toString().toLowerCase() === 'yes';

        if (
            !currentRecord.client_delivery_method.checkin_email &&
            !currentRecord.client_delivery_method.checkin_sms &&
            !currentRecord.client_delivery_method.checkin_voice
        ) {
            let deliveryError: CsvUserError = {
                value: '',
                column: '8, 9, 10',
                row: rownumber,
                message: 'Select at least one delivery channel(SMS, Voice, Email).',
                label: 'Delivery: '
            };
            this.userCsvErrors.push(deliveryError);
        }

        let isEmailRequired: boolean = currentRecord.client_delivery_method.checkin_email;
        let emailAddrError = this.validationCheckError(rowData[6], isEmailRequired, 0, 0, FORM_REGEX.email);
        this.pushFieldError(emailAddrError, 'Email', rownumber, '7');
        currentRecord.email = rowData[6] || '';

        /**Delivery Voice or SMS errors start**/
        currentRecord.phone_no = this.replacePhoneValue(rowData[4]);
        currentRecord.landline = this.replacePhoneValue(rowData[5]);
        let phone_noError = this.validationCheckError(rowData[4],
            (currentRecord.client_delivery_method.checkin_sms || currentRecord.client_delivery_method.checkin_voice),
            0, 0, FORM_REGEX.phone_no
        );
        this.pushFieldError(phone_noError, 'Primary Phone', rownumber, '5');

        let landlineError = this.validationCheckError(rowData[5], false, 0, 0, FORM_REGEX.phone_no);
        this.pushFieldError(landlineError, 'Secondary Phone', rownumber, '6');
        /**Delivery Voice or SMS errors end**/

        currentRecord.interests = this.getInterestIds(selectedInterest, splittedInterest, rownumber);

        let language = rowData[11];
        let languageError = this.validationCheckError(language, true, 0, 0, undefined, (this._orgLanguages.includes(language)));
        this.pushFieldError(languageError, 'Language', rownumber, '12');
        const englishLang = this._orgLanguages.find(lang => lang?.toLowerCase() === 'english');
        let lngValue = englishLang || (this._orgLanguages?.length ? this._orgLanguages[0] : '');
        currentRecord.lang = language.length ? language : lngValue;

        currentRecord = {
            ...currentRecord,
            ...this.checkValidDemographicFields(rowData, rownumber, selectedRace?.length ? selectedRace : rowData[14])
        };
        /*Notes validation */
        let noteText = rowData[17];
        let noteError = this.validationCheckError(noteText, false, 500, 0);
        this.pushFieldError(noteError, 'Notes', rownumber, '18');
        currentRecord.notes = noteText.length ? noteText : '';

        /*Client ID validation */
        let clientIDText = rowData[18];
        let clientIDError = this.validationCheckError(clientIDText, false, 50, 0, FORM_REGEX.alphaNumeric);
        this.pushFieldError(clientIDError, 'Client ID', rownumber, '19');
        currentRecord.client_id = clientIDText.length ? clientIDText : '';


        /** Snap validations Start **/
        if (snap) {
            currentRecord.snap = {
                ...this.getSnapValues(rowData, rownumber)
            };
        }
        /** Snap validations end **/

        /** Caregiver fields — always at the last 3 positions **/
        const caregiverOffset = snap ? 23 : 20;
        currentRecord.caregiver_name = (rowData[caregiverOffset] || '').trim();
        currentRecord.caregiver_email = (rowData[caregiverOffset + 1] || '').trim();
        currentRecord.caregiver_phone = (rowData[caregiverOffset + 2] || '').trim();

        
        return currentRecord;
    }

    // Format using reusable function
    padTo2Digits(num: number) {
        return num.toString().padStart(2, '0');
    }

    // Checks Date is valid or not.
    dateIsValid(date: string) {
        let dateFormat = new Date(date);
        let formatted = [this.padTo2Digits(dateFormat.getMonth() + 1), this.padTo2Digits(dateFormat.getDate()), dateFormat.getFullYear()].join('/');

        return formatted === date;
    }
    
    checkValidDemographicFields(rowData: string[], rowNumber: number, selectedRace: string): UserDemographicFields {
        let demographicData = new UserDemographicFields();
        /*Birth Date validation */
        let birthDateValue = rowData[12];
        let birthDateError = this.validationCheckError(birthDateValue, false, 0, 0, undefined, (!!birthDateValue?.length && this.dateIsValid(birthDateValue) && (new Date(birthDateValue) < new Date())));
        this.pushFieldError(birthDateError, 'Date of birth(mm/dd/yyyy)', rowNumber, '13');
        demographicData.birthdate = birthDateValue;

        /*Zip validation */
        let zipValue = rowData[13];
        let zipError = this.validationCheckError(zipValue, false, 0, 0, FORM_REGEX.zip);
        this.pushFieldError(zipError, 'Zip code', rowNumber, '14');
        demographicData.zipcode = zipValue;

        /*Race/Ethnicity validation */
        let raceError = this.validationCheckError(selectedRace, false, 0, 0, undefined, (this._userRaces.value.includes(selectedRace)));
        this.pushFieldError(raceError, 'Race/Ethnicity', rowNumber, '15');
        demographicData.race = selectedRace;

        /*Disability validation */
        let disabilityValue = rowData[15];
        if (disabilityValue) {
            let disabilityError = this.validationCheckError(disabilityValue, false, 0, 0, FORM_REGEX.snap);
            this.pushFieldError(disabilityError, 'Disability', rowNumber, '16');

            if (disabilityValue.length && disabilityValue == "Yes") demographicData.disable = true;
            else if (disabilityValue.length && disabilityValue == "No") demographicData.disable = false;
        }
        /*Veteran validation */
        let veteranValue = rowData[16];
        if (veteranValue) {
            let veteranError = this.validationCheckError(veteranValue, false, 0, 0, FORM_REGEX.snap);
            this.pushFieldError(veteranError, 'Veteran', rowNumber, '17');

            if (veteranValue.length && veteranValue == "Yes") demographicData.veteran = true;
            else if (veteranValue.length && veteranValue == "No") demographicData.veteran = false;
        }
        return demographicData;
    }

    private pushFieldError(fError: CsvUserError | null, fLabel: string, fRow: number, fColumn: string) {
        if (fError) {
            this.userCsvErrors.push({ ...fError, label: fLabel, row: fRow, column: fColumn });
        }
    };

    /**Prepares the error string for error type.**/
    validationCheckError(data: string, required: boolean, maxlength?: number, minlength?: number, pattern?: RegExp, valid: boolean = true): CsvUserError | null {
        let errors = new CsvUserError();
        if (!data) {
            if (required) errors.message += ' is required.';
        } else if (!valid) {
            errors.message += ' is not valid.';
        } else {
            if (pattern && !pattern.test(data.toLowerCase())) errors.message += ' is not valid.';
            if (maxlength && data.length > maxlength) errors.message += ' must be at most ' + maxlength + ' characters.';
            else if (minlength && data.length < minlength) errors.message += ' must be at least ' + minlength + ' characters.';
        }
        if (errors.message !== '') {
            errors.value = data;
            return errors;
        }
        return null;
    }

    replacePhoneValue(value: string) {
        if (!value?.length) return value;
        let replacedPh = value.replace(FORM_REGEX.phone_no, '$2$3$4');
        return `+1${replacedPh}`;
    }
    
    convertMomentToDateOnly(date: any): string | null {
        if (date) {
            const momentDate = moment(date);
            return `${momentDate.month() + 1}-${momentDate.date()}-${momentDate.year()}`;
        }
        return null;
    }

    /** SINGLE USER ARCHITECTURE API's */
    getAllOrgUsersV2(orgId: number, reqPayload: ListFilterPayload = {}, center?: number): Observable<GetOrgUserList> {
        if (center != undefined) {
            reqPayload = { ...reqPayload, programIds: [center] };
        }
        return this._httpService.getAllOrgUsersV2(orgId, reqPayload);
    }

    getUserByOrgMemberIdV2(orgId: number, memberId: string): Observable<UserFormData> {
        return this._httpService.getUserByOrgMemberIdV2(orgId, memberId).pipe(map(res => this.convertToFormData(res.data)));
    }

    addOrgUserV2(userData: OrgUserData): Observable<GeneralDataResponse<any>> {
        return this._httpService.addOrgUserV2(userData);
    }

    updateOrgUserV2(userData: OrgUserData): Observable<GeneralDataResponse<any>> {
        return this._httpService.updateOrgUserV2(this.userDetails.member_ref, userData);
    }

    deleteUserByMemberIdV2(memberId: string): Observable<GeneralResponse> {
        return this._httpService.deleteUserByMemberIdV2(memberId);
    }

    updateRemoveClientInterests(payload: any, orgId: number): Observable<GeneralDataResponse<any>> {
        return this._httpService.updateRemoveClientInterests(payload, orgId);
    }

}